import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchComments,
  fetchCommentsCount,
  fetchDeleteComment,
  IComment,
} from "@/lib/community";
import MediaSlider from "./MediaSlider";
import CommentForm from "./CommentForm";
import CustomAvatar from "./CustomAvatar";
import LikeButton from "./LikeButton";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { formatDate } from "@/utils/formatDate";
import { MixpanelActions } from "../../integrations/mixpanel/mixpanel";
import ProfilePopup from "../ui/ProfilePopup";
import { useUser } from "@supabase/auth-helpers-react";

interface IProps {
  comment: IComment;
  postId: number | string;
  depth?: number;
  id: "string";
}

const MAX_VISIBLE_INDENT = 2;

export default function Comment({ comment, postId, depth = 0, id }: IProps) {
  const { t } = useTranslation();
  const user = useUser();
  const queryClient = useQueryClient();
  const [isReplying, setIsReplying] = useState(false);
  const [collapsed, setCollapsed] = useState(
    depth <= MAX_VISIBLE_INDENT ? false : true
  );
  const [hovered, setHovered] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const commentMedia = comment.media
    ? comment.media.map((e) => JSON.parse(e))
    : [];

  const { data: repliesCount = 0 } = useQuery<number>({
    queryKey: ["commentsCount", postId, comment.id],
    queryFn: () => fetchCommentsCount(postId, comment.id),
    enabled: !!postId && !!comment,
  });

  const { data: comments = [], refetch: refetchReplies } = useQuery<IComment[]>(
    {
      queryKey: ["comments", postId, comment.id],
      queryFn: () => fetchComments(postId, comment.id),
      enabled: !collapsed && repliesCount > 0,
    }
  );

  if (deleted || !comment || comment.is_deleted) return null;

  const handleAddComment = () => {
    refetchReplies();
    setIsReplying(false);
    setCollapsed(false);
  };

  const handleDeleteComment = async () => {
    const { error } = await fetchDeleteComment(comment.id);
    if (!error) {
      queryClient.invalidateQueries({ queryKey: ["posts"] });
      setDeleted(true);
    }
  };
  return (
    <>
      <div
        id={id}
        key={comment.id}
        className={`
            flex gap-1 mt-2 sm:mt-3
            ${depth > 0 ? "ml-2 sm:ml-6" : ""}
          `}
      >
        <div className="flex flex-col items-center">
          <hr className="h-full bg-slate-400/50 w-[2px] border-0" />
        </div>

        <div className="flex flex-col w-full">
          <div className="bg-background p-2 sm:p-3 rounded-xl w-full  dark:text-white border-gray-600">
            <Link
              to={`/user/${comment.author}`}
              className="flex items-center gap-2"
              onClick={() => MixpanelActions.clickUserProfile()}
            >
              <CustomAvatar userData={comment.authorData} size="sm" />
              <div
                className="text-sm sm:text-base font-semibold text-primary"
                onMouseEnter={() => {
                  setHovered(true);
                }}
                onMouseLeave={() => {
                  setHovered(false);
                }}
              >
                {comment.authorData.username}
                {hovered && (
                  <ProfilePopup
                    authorData={comment.authorData}
                    authorId={comment.author}
                  />
                )}
                <p className="text-xs text-black dark:text-white">
                  {formatDate(comment?.created_at, t)}
                </p>
              </div>
            </Link>
            <div className="text-base text-gray-800 mt-2 break-words dark:text-gray-400">
              {comment.content}
            </div>
            {commentMedia?.length > 0 && (
              <div className="mt-1 sm:mt-2">
                <MediaSlider media={commentMedia} size={5} blocks={6} />
              </div>
            )}
            <div className="flex gap-2 mt-1 sm:mt-2 items-center flex-wrap">
              <LikeButton parentId={comment.id} parentType="comment" />
              <div
                className="text-xs text-gray-500 cursor-pointer"
                onClick={() => setIsReplying(true)}
              >
                {t("community.comment.reply")}
              </div>
              {user.id === comment.author && (
                <div
                  className="text-xs text-gray-500 cursor-pointer"
                  onClick={handleDeleteComment}
                >
                  {t("community.delete")}
                </div>
              )}
              {repliesCount > 0 && (
                <button
                  className="text-xs text-blue-600 hover:underline ml-2"
                  onClick={() => setCollapsed((v) => !v)}
                  type="button"
                >
                  {collapsed
                    ? t("community.comment.showReplies", {
                        count: repliesCount,
                      })
                    : t("community.comment.hideReplies")}
                </button>
              )}
            </div>
          </div>
          {isReplying && (
            <div className="mt-2">
              <CommentForm
                postId={postId}
                onCommentAdd={handleAddComment}
                parentId={comment.id}
                onCancel={() => setIsReplying(false)}
              />
            </div>
          )}
          {!collapsed && comments.length > 0 && depth <= 2 && (
            <div className="mt-2 space-y-2">
              {comments.map((reply) => (
                <Comment
                  key={reply.id}
                  comment={reply}
                  postId={postId}
                  depth={depth + 1}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {!collapsed && comments.length > 0 && depth > 2 && (
        <div className="mt-2 space-y-2">
          {comments.map((reply) => (
            <Comment
              key={reply.id}
              comment={reply}
              postId={postId}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </>
  );
}
